/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { DiscountCardExclusionTranslationFields } from "../discount_card_exclusions/translation_fields";
import { SPECIAL_PERQ_GROUPS_I18N_NAMESPACE } from "../../pages/discount_cards/shared/specialPerqGroupsI18n";
import {
  FieldPair,
  StyledEditField,
  StyledFieldLabel,
  StyledReadOnlyField,
  createStyledReadOnlyFieldProps,
} from "../translation_modal/styles";
import { createSpanishFieldProps } from "../translation_modal/utils";

const ruleCardSx = {
  backgroundColor: "#f9f9f9",
  borderRadius: "8px",
  marginBottom: "16px",
  padding: "16px",
};

const specialPerqGroupsKey = (key) =>
  `${SPECIAL_PERQ_GROUPS_I18N_NAMESPACE}.${key}`;

export const DiscountCardVariantTranslationFields = ({
  getExclusionTranslation,
  getRuleTranslation,
  isReadOnly,
  onExclusionTranslationChange,
  onRuleTranslationChange,
  translateOr,
  variant,
  index,
  testIdPrefix = "",
}) => {
  if (!variant) {
    return null;
  }

  const buildTestId = (suffix) =>
    testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix;

  return (
    <Box sx={{ marginTop: "24px" }}>
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: 600,
          lineHeight: "24px",
          color: "#323232",
          marginBottom: "16px",
        }}
      >
        {`${translateOr(specialPerqGroupsKey("title"), "Special Perq Groups")} ${index + 1}`}
      </Typography>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          marginBottom: "12px",
          color: "#666",
        }}
      >
        {variant.name ||
          translateOr(
            specialPerqGroupsKey("default-name"),
            `Special Perq Group ${index + 1}`,
            {
              index: index + 1,
            },
          )}
      </Typography>

      {variant.rules.length > 0 && (
        <Box sx={{ marginTop: "16px" }}>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: "24px",
              color: "#323232",
              marginBottom: "16px",
            }}
          >
            {translateOr(
              "discount-cards.create.form.rules.rules",
              "Categories",
            )}
          </Typography>

          {variant.rules.map((rule, ruleIndex) => (
            <Box key={rule.id} sx={ruleCardSx}>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "12px",
                  color: "#666",
                }}
              >
                {translateOr(
                  "discount-cards.create.form.rules.heading",
                  "Category {{index}} - {{category}}",
                  {
                    index: ruleIndex + 1,
                    category: rule.category,
                  },
                )}
              </Typography>

              <Box sx={{ marginBottom: "12px" }}>
                <StyledFieldLabel>
                  {translateOr(
                    "discount-cards.create.form.rules.property",
                    "Category",
                  )}
                </StyledFieldLabel>
                <FieldPair>
                  <StyledReadOnlyField
                    fullWidth
                    value={rule.category}
                    disabled
                    variant="outlined"
                    size="small"
                    label="English"
                    {...createStyledReadOnlyFieldProps()}
                  />
                  <StyledEditField
                    fullWidth
                    value={getRuleTranslation(
                      variant.id,
                      rule.id,
                      "spanish_category",
                    )}
                    onChange={(event) =>
                      onRuleTranslationChange(
                        variant.id,
                        rule.id,
                        "spanish_category",
                        event.target.value,
                      )
                    }
                    variant="outlined"
                    size="small"
                    label="Español"
                    {...createSpanishFieldProps(rule.category, isReadOnly)}
                  />
                </FieldPair>
              </Box>

              <Box sx={{ marginBottom: "12px" }}>
                <StyledFieldLabel>
                  {translateOr(
                    "discount-cards.create.form.rules.order-type",
                    "Applies To",
                  )}
                </StyledFieldLabel>
                <FieldPair>
                  <StyledReadOnlyField
                    data-testid={buildTestId(
                      `variant-${rule.id}-applies-to-readonly`,
                    )}
                    fullWidth
                    value={rule.applies_to}
                    disabled
                    variant="outlined"
                    size="small"
                    label="English"
                    {...createStyledReadOnlyFieldProps()}
                  />
                  <StyledEditField
                    data-testid={buildTestId(`variant-${rule.id}-applies-to`)}
                    fullWidth
                    value={getRuleTranslation(
                      variant.id,
                      rule.id,
                      "spanish_applies_to",
                    )}
                    onChange={(event) =>
                      onRuleTranslationChange(
                        variant.id,
                        rule.id,
                        "spanish_applies_to",
                        event.target.value,
                      )
                    }
                    variant="outlined"
                    size="small"
                    label="Español"
                    {...createSpanishFieldProps(rule.applies_to, isReadOnly)}
                  />
                </FieldPair>
              </Box>

              <Box sx={{ marginBottom: "12px" }}>
                <StyledFieldLabel>
                  {translateOr(
                    "discount-cards.create.form.rules.number-of-guests",
                    "Number of Guests",
                  )}
                </StyledFieldLabel>
                <FieldPair>
                  <StyledReadOnlyField
                    data-testid={buildTestId(
                      `variant-${rule.id}-number-of-guests-readonly`,
                    )}
                    fullWidth
                    value={rule.number_of_guests}
                    disabled
                    variant="outlined"
                    size="small"
                    label="English"
                    {...createStyledReadOnlyFieldProps()}
                  />
                  <StyledEditField
                    data-testid={buildTestId(
                      `variant-${rule.id}-number-of-guests`,
                    )}
                    fullWidth
                    value={getRuleTranslation(
                      variant.id,
                      rule.id,
                      "spanish_number_of_guests",
                    )}
                    onChange={(event) =>
                      onRuleTranslationChange(
                        variant.id,
                        rule.id,
                        "spanish_number_of_guests",
                        event.target.value,
                      )
                    }
                    variant="outlined"
                    size="small"
                    label="Español"
                    {...createSpanishFieldProps(rule.number_of_guests, isReadOnly)}
                  />
                </FieldPair>
              </Box>

              <Box sx={{ marginBottom: "0px" }}>
                <StyledFieldLabel>
                  {translateOr(
                    "discount-cards.create.form.rules.discount-section",
                    translateOr(
                      "discount-cards.create.form.rules.discounts",
                      "Discount",
                    ),
                  )}
                </StyledFieldLabel>
                <FieldPair>
                  <StyledReadOnlyField
                    data-testid={buildTestId(
                      `variant-${rule.id}-discount-readonly`,
                    )}
                    fullWidth
                    value={rule.discount}
                    disabled
                    variant="outlined"
                    size="small"
                    label="English"
                    {...createStyledReadOnlyFieldProps()}
                  />
                  <StyledEditField
                    data-testid={buildTestId(`variant-${rule.id}-discount`)}
                    fullWidth
                    value={getRuleTranslation(
                      variant.id,
                      rule.id,
                      "spanish_discount",
                    )}
                    onChange={(event) =>
                      onRuleTranslationChange(
                        variant.id,
                        rule.id,
                        "spanish_discount",
                        event.target.value,
                      )
                    }
                    variant="outlined"
                    size="small"
                    label="Español"
                    {...createSpanishFieldProps(rule.discount, isReadOnly)}
                  />
                </FieldPair>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <DiscountCardExclusionTranslationFields
        descriptionLabel={translateOr(
          "discount-cards.create.form.exclusion-description",
          "Description",
        )}
        exclusions={variant.exclusion_items}
        getTranslation={getExclusionTranslation}
        isReadOnly={isReadOnly}
        onTranslationChange={onExclusionTranslationChange}
        testIdPrefix={buildTestId("variant")}
        sectionTitle={translateOr(
          "discount-cards.create.form.exclusions",
          "Exclusions",
        )}
        translateOr={translateOr}
        variantId={variant.id}
      />
    </Box>
  );
};

DiscountCardVariantTranslationFields.propTypes = {
  getExclusionTranslation: PropTypes.func.isRequired,
  getRuleTranslation: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  onExclusionTranslationChange: PropTypes.func.isRequired,
  onRuleTranslationChange: PropTypes.func.isRequired,
  testIdPrefix: PropTypes.string,
  translateOr: PropTypes.func.isRequired,
  variant: PropTypes.shape({
    exclusion_items: PropTypes.array.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    rules: PropTypes.array.isRequired,
  }),
};
